/**
 * 记录案例。列表走后端 `records.list`，该 action 要求已登录
 * （后端是 requireSignedInOwner(ownerId)），未登录时按未登录态呈现，不发请求。
 */
import { useCallback, useEffect, useState } from "react";
import { callApi, getSession } from "../api";
import type { BaziRecord } from "../data/record";

type State = "idle" | "loading" | "done" | "error" | "anonymous";

export function RecordsPage({ onNavigate }: { onNavigate: (route: string) => void }) {
  const [items, setItems] = useState<BaziRecord[]>([]);
  const [state, setState] = useState<State>("idle");
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    if (!getSession()) {
      setState("anonymous");
      return;
    }
    setState("loading");
    const result = await callApi<{ records?: BaziRecord[] }>("records.list");
    if (result.ok) {
      setItems(result.records ?? []);
      setState("done");
    } else {
      setMessage(result.error);
      setState("error");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function toggleStar(record: BaziRecord) {
    if (!record.id) return;
    // 先本地翻转，请求失败再重新拉取校正，避免点一下等一圈
    setItems((prev) =>
      prev.map((item) => (item.id === record.id ? { ...item, starred: !item.starred } : item)),
    );
    const result = await callApi("records.star", { id: record.id, starred: !record.starred });
    if (!result.ok) void load();
  }

  async function remove(record: BaziRecord) {
    if (!record.id) return;
    if (!window.confirm(`确认删除「${record.name ?? "未命名案例"}」？删除后不可恢复。`)) return;
    const result = await callApi("records.delete", { id: record.id });
    if (result.ok) setItems((prev) => prev.filter((item) => item.id !== record.id));
    else setMessage(result.error);
  }

  return (
    <main className="page records-page">
      <h1>记录案例</h1>
      <p className="page-summary">保存你整理过的四柱资料与学习笔记，便于日后复盘比对。</p>

      {state === "anonymous" && (
        <p className="empty-state">
          记录保存在账号下，登录后可查看与同步。
          <button type="button" onClick={() => onNavigate("auth")}>
            去登录
          </button>
        </p>
      )}
      {state === "loading" && <p className="empty-state">正在加载记录…</p>}
      {state === "error" && <p className="form-error">加载失败：{message}</p>}
      {state === "done" && items.length === 0 && (
        <p className="empty-state">
          还没有记录。
          <button type="button" onClick={() => onNavigate("paipan")}>
            去排盘
          </button>
        </p>
      )}
      {state === "done" && items.length > 0 && (
        <ul className="record-list">
          {items.map((item, index) => (
            <li key={item.id ?? index}>
              <div className="record-main">
                <strong>{item.name || "未命名案例"}</strong>
                {item.pillarInput && <span className="record-pillars">{item.pillarInput}</span>}
                {item.birthAt && <span className="record-birth">{item.birthAt}</span>}
                {item.sex && <span className="record-sex">{item.sex}</span>}
              </div>
              <div className="record-actions">
                <button type="button" onClick={() => toggleStar(item)}>
                  {item.starred ? "已收藏" : "收藏"}
                </button>
                <button type="button" onClick={() => remove(item)}>
                  删除
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}
